import { use, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDbClient } from "@tanstack/react-db";
import { useBrowserSession } from "@softmax/web-features/session";
import { QueryViewerContext } from "@softmax/web-features/data";
import { profileImageError } from "./profile-image";
import { refreshAccountResources } from "./refresh-account-resources";
import { writeFailureNotice, type ProfileNotice } from "./profile-notice";

export function useAccountWrites() {
  const queryClient = useQueryClient();
  const dbClient = useDbClient();
  const viewerId = use(QueryViewerContext);
  const browserSession = useBrowserSession();
  const { refresh: refreshSession } = browserSession;
  const [notices, setNotices] = useState<Record<string, ProfileNotice>>({});
  const [pending, setPending] = useState(false);
  const [uncertainKey, setUncertainKey] = useState<string | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const operation = useRef<"idle" | "writing" | "recovering" | "uncertain">(
    "idle",
  );
  const busy = pending || recovering;

  function note({ key, notice }: { key: string; notice: ProfileNotice }) {
    setNotices((previous) => ({ ...previous, [key]: notice }));
  }

  function canWrite() {
    return (
      browserSession.kind === "authenticated" && operation.current === "idle"
    );
  }

  async function run({
    key,
    progress,
    done,
    work,
  }: {
    key: string;
    progress: string;
    done: string;
    work: () => Promise<void>;
  }) {
    if (!canWrite()) return false;

    operation.current = "writing";
    setPending(true);
    note({ key, notice: { tone: "muted", text: progress } });
    let uncertain = false;

    try {
      await work();
      note({ key, notice: { tone: "saved", text: done } });
      return true;
    } catch (failure) {
      const failureNotice = writeFailureNotice(failure);
      uncertain = failureNotice.uncertain;

      if (uncertain) setUncertainKey(key);

      note({ key, notice: failureNotice.notice });
      return false;
    } finally {
      operation.current = uncertain ? "uncertain" : "idle";
      setPending(false);
    }
  }

  async function refreshSaved() {
    if (operation.current === "writing" || operation.current === "recovering")
      return;

    operation.current = "recovering";
    setRecovering(true);
    setRefreshError(null);
    const result = await refreshAccountResources({
      queryClient,
      dbClient,
      viewerId,
    });

    if (result.kind === "failed") {
      setRefreshError(result.message);
    } else {
      if (uncertainKey)
        note({
          key: uncertainKey,
          notice: {
            tone: "muted",
            text: "Saved values refreshed. Your draft is kept.",
          },
        });

      setUncertainKey(null);
      void refreshSession();
    }

    setRecovering(false);
    operation.current =
      result.kind === "failed" && uncertainKey ? "uncertain" : "idle";
  }

  function acceptable({
    key,
    file,
  }: {
    readonly key: string;
    readonly file: File;
  }) {
    const error = profileImageError(file);

    if (error === null) return true;

    note({ key, notice: { tone: "error", text: error } });
    return false;
  }

  return {
    busy,
    locked:
      browserSession.kind !== "authenticated" || busy || uncertainKey !== null,
    uncertain: uncertainKey !== null,
    recovering,
    refreshError,
    notices,
    note,
    canWrite,
    run,
    refreshSaved,
    refreshSession,
    acceptable,
  };
}

export type AccountWrites = ReturnType<typeof useAccountWrites>;
